import React, { useEffect, useState } from 'react';

interface AmountInputProps {
  amount: number;
  onChangeAmount: (amount: number) => void;
  imageNode?: React.ReactNode;
  max?: number;
  unitLabel?: string;
}

const AmountInput: React.FC<AmountInputProps> = ({
  amount,
  onChangeAmount,
  imageNode,
  max,
  unitLabel,
}) => {
  const [draftAmount, setDraftAmount] = useState(String(amount));

  useEffect(() => {
    setDraftAmount(String(amount));
  }, [amount]);

  const parsePositive = (value: string): number | null => {
    if (value.trim() === '') return null;
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  };

  const clampToMax = (value: number): number => {
    if (max === undefined) return value;
    return Math.min(value, max);
  };

  const commitDraft = () => {
    const parsed = parsePositive(draftAmount);
    const nextAmount = clampToMax(parsed ?? amount);
    onChangeAmount(nextAmount);
    setDraftAmount(String(nextAmount));
  };

  const handleInputChange = (value: string) => {
    setDraftAmount(value);
    const parsed = parsePositive(value);
    if (parsed !== null) {
      onChangeAmount(clampToMax(parsed));
    }
  };

  const handleIncrease = () => {
    const parsed = parsePositive(draftAmount);
    const base = parsed ?? amount;
    const nextAmount = clampToMax(base + 1);
    onChangeAmount(nextAmount);
    setDraftAmount(String(nextAmount));
  };

  const handleDecrease = () => {
    const parsed = parsePositive(draftAmount);
    const base = parsed ?? amount;
    const nextAmount = Math.max(1, base - 1);
    onChangeAmount(nextAmount);
    setDraftAmount(String(nextAmount));
  };

  return (
    <div className="purchase-controls__amount-section">
      {unitLabel ? (
        <span className="purchase-controls__amount-unit">
          Purchase unit: {unitLabel}
        </span>
      ) : null}
      <div className="purchase-controls__amount-container">
        {imageNode}
        <input
          type="number"
          value={draftAmount}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={commitDraft}
          min="1"
          max={max}
          inputMode="decimal"
          className="purchase-controls__amount-input"
        />
        <div className="purchase-controls__amount-buttons">
          <button
            type="button"
            onClick={handleIncrease}
            className="purchase-controls__amount-btn purchase-controls__amount-btn--increase"
            disabled={max !== undefined && amount >= max}
          >
            +
          </button>
          <button
            type="button"
            onClick={handleDecrease}
            className="purchase-controls__amount-btn purchase-controls__amount-btn--decrease"
            disabled={amount <= 1}
          >
            −
          </button>
        </div>
      </div>
    </div>
  );
};

export default AmountInput;
